import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const RefundVoucher = ({ data }) => {
  const voucherRef = useRef(null);
  const [templateConfig, setTemplateConfig] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get("/api/templates/active/refund");
        if (res.data) {
          setTemplateConfig(res.data.config || {});
        }
      } catch (err) {
        console.error("Failed to load active refund template:", err);
      }
    };
    fetchTemplate();
  }, []);

  if (!data) return null;

  const customerName = data.customer?.full_name || "N/A";
  const customerPhone = data.customer?.phone_number || "N/A";
  const customerEmail = data.customer?.email || "N/A";

  const salesAmount = Number(data.sale?.amount ?? 0).toFixed(2);
  const tax = Number(data.sale?.tax ?? 0).toFixed(2);

  const totalSupplierFee = data.items?.reduce(
    (sum, item) => sum + Number(item?.supplier_cancellation_fee ?? 0),
    0
  ) ?? 0;

  const totalCustomerFee = data.items?.reduce(
    (sum, item) => sum + Number(item?.customer_cancellation_fee ?? 0),
    0
  ) ?? 0;

  const totalCancellationFee = (totalSupplierFee + totalCustomerFee).toFixed(2);
  const refundAmount = Number(data.refund_amount ?? 0).toFixed(2);

  const handleSaveVoucher = () => {
    if (!voucherRef.current) return;
    const input = voucherRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Refund_Voucher_${customerName.replace(/\s+/g, "_")}.pdf`);
    });
  };

  const config = templateConfig || {};
  const areaStyles = config.areaStyles || [];
  
  const getStyle = (areaName) => {
    const area = areaStyles.find(a => a.area === areaName);
    if (!area) return {};
    return {
      backgroundColor: area.bgColor,
      color: area.fontColor,
      fontSize: `${area.fontSize}px`,
      fontFamily: config.defaultFont || "Arial",
    };
  };

  const globalStyle = {
    fontFamily: config.defaultFont || "Arial",
    color: config.defaultFontColor || "#000",
    fontSize: `${config.defaultFontSize || 12}px`,
    lineHeight: "1.5",
  };

  return (
    <div className="refund-voucher-viewer p-4">
      <div className="flex justify-end mb-4 no-print gap-2">
        <button onClick={() => window.print()} className="btn btn-outline-secondary">Print</button>
        <button onClick={handleSaveVoucher} className="btn btn-primary">📄 Download PDF</button>
      </div>

      <div className="voucher-outer-wrapper" style={{background: "#e0e0e0", padding: "40px", display: "flex", justifyContent: "center"}}>
        <div 
          ref={voucherRef} 
          className="voucher-inner-container shadow-lg" 
          style={{
            ...globalStyle, 
            width: "210mm", 
            minHeight: "297mm",
            backgroundColor: "#fff", 
            padding: "15mm",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {/* HEADER SECTION */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-4 items-center">
              {config.logoUrl ? (
                <img src={config.logoUrl} alt="Logo" style={{maxHeight: 70}} crossOrigin="anonymous"/>
              ) : (
                <img src={process.env.PUBLIC_URL + "/rekonic-logo.jpg"} alt="Rekonic Logo" style={{maxHeight: 60}} />
              )}
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/IATA_logo.svg/320px-IATA_logo.svg.png" alt="IATA" style={{maxHeight: 50}} />
            </div>

            <div className="text-right flex flex-col items-end">
              <div className="qr-code mb-2" style={{width: 80, height: 80, border: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${customerName}`} alt="QR" />
              </div>
              <div className="text-[10px] leading-tight">
                <p className="font-bold text-[#800000]">REFUND#: {data.id}</p>
                <p className="font-bold text-[#800000]">{data.id} :رقم الاسترداد</p>
                <p className="mt-1">TRN#: 100519322000003</p>
                <p>100519322000003 :رقم التسجيل الضريبي</p>
              </div>
            </div>
          </div>

          {/* GREETINGS */}
          <div className="mb-4 text-[11px] leading-relaxed text-blue-900 font-medium">
            <p>Dear Valued Customer,</p>
            <p>Greetings from REKONIC TRAVEL LLC -</p>
            <p>(ريكونيك ترافيل ش م ح) .</p>
          </div>

          {/* TITLE */}
          <div className="text-center mb-6 py-1 border-y-2 border-gray-100">
            <h2 className="text-lg font-bold text-black uppercase m-0">REFUND VOUCHER - قسيمة استرداد</h2>
          </div>

          {/* CUSTOMER & SUMMARY BOX */}
          <div className="flex justify-between mb-6">
            <div className="customer-box flex-1">
              <h3 className="text-sm font-bold text-blue-900 mb-1 uppercase">{customerName}</h3>
              <div className="text-[11px] text-gray-700 leading-tight">
                <p>SHARJAH</p>
                <p>{customerPhone}</p>
                <p>ACC#: {customerName}</p>
              </div>
            </div>

            <div className="summary-box w-[45%]">
              <table className="w-full border-collapse border border-blue-900 text-[10px]">
                <thead>
                  <tr className="bg-[#002040] text-white">
                    <th className="border border-blue-900 p-1 text-left">REFUND DATE<br/>تاريخ الاسترداد</th>
                    <th className="border border-blue-900 p-1 text-center bg-[#002040]">NET REFUND<br/>صافي المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center h-10">
                    <td className="border border-blue-900 p-1">{new Date().toLocaleDateString()}</td>
                    <td className="border border-blue-900 p-1 bg-[#002040] text-white font-bold text-sm">AED {refundAmount}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* REFUND DETAILS TABLE */}
          <div className="flex-grow">
            <h4 className="text-[10px] font-bold uppercase text-gray-600 mb-1">REFUND DETAILS - تفاصيل الاسترداد</h4>
            <table className="w-full border-collapse border border-blue-900 text-[10px]">
              <thead className="bg-[#002040] text-white uppercase">
                <tr>
                  <th className="border border-blue-900 p-2 text-left">DESCRIPTION<br/>الوصف</th>
                  <th className="border border-blue-900 p-2 text-right w-32">AMOUNT (AED)<br/>المبلغ</th>
                </tr>
              </thead>
              <tbody>
                <tr className="h-12">
                  <td className="border border-blue-900 p-2">Original Sales Amount</td>
                  <td className="border border-blue-900 p-2 text-right">{salesAmount}</td>
                </tr>
                <tr className="h-12">
                  <td className="border border-blue-900 p-2">Tax (VAT)</td>
                  <td className="border border-blue-900 p-2 text-right">{tax}</td>
                </tr>
                <tr className="h-12 bg-red-50">
                  <td className="border border-blue-900 p-2 text-red-700 font-bold">Cancellation Fee (Supplier + Agency)</td>
                  <td className="border border-blue-900 p-2 text-right text-red-700 font-bold">-{totalCancellationFee}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td className="border border-blue-900 p-2 text-right uppercase">Total Refund Amount (AED)</td>
                  <td className="border border-blue-900 p-2 text-right text-blue-900 text-sm">{refundAmount}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ASSETS SECTION (Stamp & Sign) */}
          <div className="flex justify-between items-center mb-8 mt-8">
            <div className="flex gap-4">
               <div className="text-center">
                  {config.addSignature !== false && config.signatureUrl ? (
                    <img src={config.signatureUrl} alt="Signature" style={{maxHeight: 70}} crossOrigin="anonymous"/>
                  ) : (
                    <div className="h-16 w-32 border-b-2 border-gray-200"></div>
                  )}
                  <div className="mt-2 text-[10px] font-bold text-blue-900 uppercase">Authorised By<br/><span className="text-gray-400 font-normal">المفوض بالتوقيع</span></div>
               </div>
               
               {config.addStamp !== false && config.stampUrl && (
                  <img src={config.stampUrl} alt="Stamp" style={{maxHeight: 100}} crossOrigin="anonymous"/>
               )}
            </div>

            <div className="company-contact text-right text-[10px] text-blue-900 leading-tight">
               <p className="font-bold">REKONIC TRAVEL LLC</p>
               <p>Sharjah, United Arab Emirates</p>
               <p>Support: info@rekonic.com</p>
               <p>Phone: +971 XXXXXXXX</p>
            </div>
          </div>

          {/* BANK INFO */}
          <div className="mt-auto">
            <table className="w-full border-collapse border border-blue-900 text-[9px]">
              <thead>
                <tr className="bg-[#002040] text-white">
                  <th colSpan="2" className="border border-blue-900 p-1 text-center">BANK ACCOUNT INFORMATION - بيانات الحساب البنكي</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50 w-1/4">Account Title</td>
                  <td className="border border-blue-900 p-1 uppercase font-bold">REKONIC TRAVEL LLC</td>
                </tr>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50">Account Number</td>
                  <td className="border border-blue-900 p-1 font-bold">010XXXXXXXXXX</td>
                </tr>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50">Bank Name</td>
                  <td className="border border-blue-900 p-1 font-bold uppercase">MASHREQ BANK</td>
                </tr>
              </tbody>
            </table>
            
            <div className="mt-4 text-[9px] text-blue-900 leading-tight italic text-center">
               <p>{config.termsAndConditions || "This is a computer generated refund voucher."}</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RefundVoucher;
